import React, { useEffect, useState, useRef } from "react"
import axios from "axios"
import { navigate } from "gatsby"
import styled from "styled-components"
import debounce from "lodash.debounce"

import { BorderRadius, Color, Spacing } from "@persona-id/design-tokens"
import {
  DocumentIcon,
  ListIcon,
  SearchIcon,
  XIcon,
} from "@persona-id/design-tokens/icons/16px"
import Button from "@persona-id/design-system/components/Button"
import {
  ContentfulSearchResult,
  ContentfulSearchResultContentType,
} from "../models/contentful"
import { getSearchResultLink, sortSearchResults } from "../utils/search"

const NavigationTopSearch = ({ closeSearch }: { closeSearch: () => void }) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<ContentfulSearchResult[]>(
    []
  )
  const [searchError, setSearchError] = useState(false)

  const debouncedSearch = debounce((inputQuery: string) => {
    setSearchQuery(inputQuery)
    setSearchError(false)

    if (inputQuery.length > 2) {
      axios
        .get(
          `${process.env.PERSONA_HOSTNAME}/api/internal/resource_center/article_search?query=${inputQuery}`
        )
        .then(response => {
          const searchResults = response.data["search-results"]
          setSearchResults(sortSearchResults(searchResults))
          window.analytics.track("search", {
            query: inputQuery,
          })
        })
        .catch(() => {
          setSearchError(true)
        })
    }
  }, 300)

  const handleSearchKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      navigate(`/search_results?q=${(e.target as HTMLInputElement).value}`)
      closeSearch()
    }
  }

  const inputRef = useRef(null)

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus()
    }

    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const renderSearchResults = () => {
    if (searchError) {
      return (
        <SearchResults>
          There was an error performing this search. Please try again later.
        </SearchResults>
      )
    } else if (searchResults.length > 0) {
      return (
        <SearchResults>
          {searchResults
            .slice(0, NUM_RESULTS_TO_SHOW_IN_SEARCH_DROPDOWN)
            .map((searchResult, index) => (
              <SearchResultRow
                key={`search-result-${index}}`}
                onClick={() => {
                  navigate(getSearchResultLink(searchResult))
                  closeSearch()
                }}
              >
                {searchResult["content-type"] ===
                ContentfulSearchResultContentType.HelpCenterArticle ? (
                  <DocumentIcon />
                ) : (
                  <ListIcon />
                )}
                {searchResult.title}
              </SearchResultRow>
            ))}
          <Button
            size="sm"
            variant="basic"
            disabled={searchResults.length === 0}
            onClick={() => {
              navigate(`/search_results?q=${searchQuery}`)
              closeSearch()
            }}
          >
            See all results
          </Button>
        </SearchResults>
      )
    } else {
      return null
    }
  }

  const NUM_RESULTS_TO_SHOW_IN_SEARCH_DROPDOWN = 5

  return (
    <SearchWrapper>
      <SearchInputWrapper>
        <SearchIcon />
        <SearchInput
          onChange={e => debouncedSearch(e.target.value)}
          onKeyUp={e => handleSearchKeyUp(e)}
          placeholder="Search..."
          ref={inputRef}
        />
        <SearchInputCloseIcon>
          <XIcon onClick={closeSearch} />
        </SearchInputCloseIcon>
      </SearchInputWrapper>
      {renderSearchResults()}
    </SearchWrapper>
  )
}

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: auto;
  padding: 0px ${Spacing[4]};
`

const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[1]};
  padding: ${Spacing[1]} ${Spacing[2]};
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${BorderRadius.Sm};
  width: 100%;

  :focus-within {
    border: 1px solid ${Color.CoreBlue800};
  }
`

const SearchInput = styled.input`
  width: 100%;
  border: 0px;
  font-family: "Rubik", sans-serif;

  :focus {
    outline: none;
  }
`

const SearchInputCloseIcon = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`

const SearchResults = styled.div`
  position: absolute;
  top: 32px;
  left: 16px;
  width: calc(100% - 32px);
  padding: ${Spacing[2]} ${Spacing[3]};
  display: flex;
  flex-direction: column;
  row-gap: ${Spacing[2]};
  background-color: ${Color.PrimaryWhite};
  border-radius: ${BorderRadius.Sm};
  box-shadow: 1px 2px 1px ${Color.BorderHairline};
  z-index: 2;
`

const SearchResultRow = styled.div`
  display: flex;
  column-gap: ${Spacing[2]};
  align-items: center;
  padding: ${Spacing[1]};
  cursor: pointer;
  border-radius: ${BorderRadius.Sm};

  :hover {
    background-color: ${Color.CorePurple100};
  }
`

export default NavigationTopSearch
